import {
    FillsGraphData,
    getOrderFillAtTimeOrEnd,
    getBucketWithFillAtTime,
    getBucketAtTimeOrEnd,
    fillLatestNonInterpolatedValues,
    OrderFillInterpolatedValues,
} from 'widgets/fillsGraph/fillsGraphData';
import { NumberFormat } from '@instinet-analytics/core-lib';
import { getActualValue } from 'utils/interpolation';
import { TimeFormat } from '@instinet-analytics/core-lib';
import { BucketedData } from 'widgets/fillsGraph/fillsGraphDataBuckets';
import { applyTickSize } from 'utils/tickSize';

export interface LegendData {
    price?: string;
    avgExec?: string;
    vwap?: string;
    percentDealt?: string;
    index?: string;
    fillPrice?: string;
    fillVolume?: string;
    showFillLegend: boolean;
    bucketTime?: string;
    open?: string;
    high?: string;
    low?: string;
    close?: string;
}

export function deriveLegendData(
    fillsGraphData: FillsGraphData,
    numberFormat: NumberFormat,
    timeFormat: TimeFormat,
    time: number | undefined,
    fillHoverInterval: number,
    avgExecAndVwapDecimals: number
): LegendData {
    const interpolatedValues: OrderFillInterpolatedValues = {
        price: undefined,
        avgExec: undefined,
        vwap: undefined,
        percentDealt: undefined,
        relativePerfIndex: undefined,
    };
    let showFillLegend = false;
    let bucket: BucketedData | undefined = undefined;

    if (time !== undefined) {
        const d = getOrderFillAtTimeOrEnd(fillsGraphData, time);

        if (d === undefined) {
            return {
                showFillLegend: false,
            };
        }

        const hoverBucketWithFill = getBucketWithFillAtTime(
            fillsGraphData,
            time,
            fillHoverInterval
        );
        showFillLegend = hoverBucketWithFill !== undefined;

        const fill = hoverBucketWithFill
            ? { ...d, bucket: hoverBucketWithFill }
            : d;

        interpolatedValues.price = fill.price;
        interpolatedValues.avgExec = fill.avgExec;
        interpolatedValues.vwap = fill.vwap;
        interpolatedValues.percentDealt = fill.percentDealt;
        interpolatedValues.relativePerfIndex = fill.relativePerfIndex;

        bucket = getBucketAtTimeOrEnd(fillsGraphData, time);
    } else {
        fillLatestNonInterpolatedValues(fillsGraphData, interpolatedValues);
    }

    const bucketTime = bucket
        ? `${timeFormat.toTimeMinutes(
              bucket.startTime!
          )} - ${timeFormat.toTimeMinutes(bucket.endTimeDisplay!)}`
        : undefined;

    const { tickSize } = fillsGraphData;

    return {
        price: applyTickSize(
            numberFormat,
            tickSize,
            getActualValue(interpolatedValues.price)
        ),
        open: applyTickSize(numberFormat, tickSize, bucket?.candle?.open),
        high: applyTickSize(numberFormat, tickSize, bucket?.candle?.high),
        low: applyTickSize(numberFormat, tickSize, bucket?.candle?.low),
        close: applyTickSize(numberFormat, tickSize, bucket?.candle?.close),
        avgExec: applyDecimals(
            numberFormat,
            avgExecAndVwapDecimals,
            getActualValue(interpolatedValues.avgExec)
        ),
        vwap: applyDecimals(
            numberFormat,
            avgExecAndVwapDecimals,
            getActualValue(interpolatedValues.vwap)
        ),
        percentDealt: numberFormat.n2(
            getActualValue(interpolatedValues.percentDealt)
        ),
        index: numberFormat.n4(
            getActualValue(interpolatedValues.relativePerfIndex)
        ),
        fillPrice: applyTickSize(numberFormat, tickSize, bucket?.price),
        fillVolume: numberFormat.n0(bucket?.fillVolume),
        showFillLegend,
        bucketTime,
    };
}

export function applyDecimals(
    numberFormat: NumberFormat,
    decimals: number,
    value: number | null | undefined
): string {
    if (value === undefined || value === null) {
        return '';
    }
    return numberFormat.n(decimals, value);
}
